import React, { useState } from "react";

import { client, apiEndpoints, serverUrl, assetBase } from "../../config";

import PostSingle from "../../partials/posts/postSingle";
import PostImage from "../../partials/postImage";
import ClientTheme from "../../core/clientTheme";

import { Box, Typography, Link } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import TimeIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import MailIcon from "@mui/icons-material/EmailOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import PinIcon from "@mui/icons-material/PlaceOutlined";
import Iframe from "../../partials/iframe";

export default function EventSingle(props) {
  const [post, setPost] = useState(null);

  const clientTheme = { ...ClientTheme() };

  const processTime = (time) => {
    if (time == null || time === "") return null;

    const cleaned = String(time).replace(/\n/g, "").trim();

    let day, hour;

    if (/[-T:]/.test(cleaned)) {
      const [datePart, timePart] = cleaned.split("T");
      const [year, month, dayNum] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart.split(":").map(Number);

      const date = new Date(year, month - 1, dayNum, hours, minutes);

      day = date.toLocaleString("en-GB", { dateStyle: "long" });
      hour = date.toLocaleString("en-GB", { hour12: true, timeStyle: "short" });
    } else {
      const date = new Date(parseInt(cleaned, 10) * 1000);
      if (isNaN(date.getTime())) return null;

      day = date.toLocaleString("en-GB", {
        dateStyle: "long",
        timeZone: "Europe/London",
      });
      hour = date.toLocaleString("en-GB", {
        hour12: true,
        timeStyle: "short",
        timeZone: "Europe/London",
      });
    }

    return { hour, day };
  };

  const renderEventDetails = () => {
    let start = processTime(post?.field_start_date);
    let end = processTime(post?.field_end_date);

    const iconStyle = {
      fontSize: 25,
      color: "clientPrimary.main",
    };

    // Published view resolves the featured image from the entity's published
    // dir via assetBase (same-origin on deployed hosts); preview reads the
    // draft image off the Drupal host.
    let img = "";
    if (post.field_featured_image) {
      img = props.preview
        ? `${serverUrl}/${post.field_featured_image}`
        : `${assetBase}/event/${post.id}/${post.id}/${post.field_featured_image}`;
    }

    const iconWidth = { xs: 2, sm: 1 };
    const contentWidth = { xs: 10, sm: 11 };
    return (
      <Box className="mb-5">
        <Row>
          <Col xs={4} className="d-none d-md-block">
            <PostImage
              src={img}
              nid={post.nid}
              className="ratio ratio-1x1 event-single-img-container"
            />
          </Col>
          <Col
            xs={12}
            md={8}
            className="d-flex flex-column ps-md-4 justify-content-between"
          >
            <Row className="d-flex flex-nowrap align-items-center justify-content-start mb-1 w-100">
              <Col
                className="d-flex align-items-center"
                xs={iconWidth.xs}
                sm={iconWidth.sm}
              >
                <TimeIcon sx={iconStyle} />
              </Col>
              <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                <Typography
                  className="d-flex"
                  owner="client"
                  variant="body1"
                  color="clientText.main"
                >
                  {client === "equans" ? (
                    <>
                      {post.field_start_time === post.field_end_time
                        ? post.field_end_time
                        : `${post.field_start_time} - ${post.field_end_time}`}
                    </>
                  ) : (
                    <>
                      {start?.hour === end?.hour
                        ? end?.hour
                        : `${start?.hour} - ${end?.hour}`}
                    </>
                  )}
                </Typography>
              </Col>
            </Row>
            <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
              <Col
                className="d-flex align-items-center"
                xs={iconWidth.xs}
                sm={iconWidth.sm}
              >
                <CalendarIcon sx={iconStyle} />
              </Col>
              <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                <Typography
                  className="d-flex"
                  owner="client"
                  variant="body1"
                  color="clientText.main"
                >
                  {start?.day === end?.day
                    ? end?.day
                    : `${start?.day} - ${end?.day}`}
                </Typography>
              </Col>
            </Row>
            {post.field_contact_number && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <PhoneIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      href={`tel:${post.field_contact_number}`}
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                    >
                      {post.field_contact_number}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
            {post.field_email && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <MailIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`mailto: ${post.field_email}`}
                    >
                      {post.field_email}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
            {post.field_website && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <LinkIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`${post.field_website}`}
                      target="_blank"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {post.field_website}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
            {post.field_address && (
              <Row className="d-flex flex-nowrap align-items-center justify-content-start my-1 w-100">
                <Col
                  className="d-flex align-items-center"
                  xs={iconWidth.xs}
                  sm={iconWidth.sm}
                >
                  <PinIcon sx={iconStyle} />
                </Col>
                <Col xs={contentWidth.xs} sm={contentWidth.sm}>
                  <Typography
                    className="d-flex"
                    owner="client"
                    variant="body1"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body1"
                      lh="wide"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`http://maps.google.com/?q=${post.field_address}`}
                      target="_blank"
                    >
                      {post.field_address}
                    </Link>
                  </Typography>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Box>
    );
  };

  return (
    <PostSingle
      preview={props.preview}
      apiEndpoint={apiEndpoints.events}
      entityFavoritesContentType={"event"}
      previewReturnRoute={"/events-creator"}
      returnRoute={"/events"}
      backBtnText={"Event Library"}
      screenClassName={"events"}
      v2Bundle={"event"}
      logCustomEventType={"Event"}
      entityType={"event"}
      // featuredImageEnabled={true}
      setEntityPost={(_post) => {
        setPost(_post);
      }}
      childrenAfter={
        post?.webform ? (
          <Iframe
            src={`${serverUrl}/form/${post.webform.machine_name.replace(/_/g, "-")}?app=true`}
            title={post.webform.machine_name}
          />
        ) : null
      }
    >
      {post && renderEventDetails()}
    </PostSingle>
  );
}
